import React, { useCallback } from "react"
import { Col, Row } from "react-bootstrap"
import { State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { getElementFieldValue, makeSetHandler, makeValidationHandler } from "../../globalStates/utils"
import Section from "../../components/Section"
import TextField from "../../components/Field/Text"
import ToggleSwitchField from "../../components/Field/ToggleSwitchField"
import { Organization, PrintCatalog } from "../../globalStates/ExhibitorDataTypes"

interface ContactSectionProps {
    printCatalogState: State<PrintCatalog, Validation>
    organizationState: State<Organization, Validation>
    readOnly: boolean
}

const ContactSection: React.FC<ContactSectionProps> = ({ printCatalogState, organizationState, readOnly }) => {
    const branding = useLanguageState().getStrings()
    const printCatalog = useHookstate(printCatalogState)
    const onChangeContactImported = useCallback(() => {
        printCatalog.contactImported.set(!printCatalog.contactImported.get())
        if (printCatalog.contactImported.get()) {
            printCatalog.phone.set(organizationState.phone.get())
            printCatalog.fax.set(organizationState.fax.get())
            printCatalog.email.set(organizationState.email.get())
            printCatalog.web.set(organizationState.web.get())
        }
    }, [organizationState, printCatalog])

    return (
        <Section title={branding.configuration.printPage.sctContactInformation.lblSectionContactInfo}>
            <Row>
                <Col>
                    <ToggleSwitchField
                        name="copyCompanyContact"
                        label={branding.configuration.printPage.lblCopyData}
                        value={printCatalog.contactImported.get()}
                        readonly={readOnly}
                        onChange={onChangeContactImported}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        id="phone"
                        name="phone"
                        label={branding.configuration.printPage.sctContactInformation.lblPhone}
                        placeholder={branding.configuration.printPage.sctContactInformation.hntPhone}
                        value={getElementFieldValue(printCatalog, "phone")}
                        onChange={makeSetHandler(printCatalog, "phone")}
                        readonly={printCatalog.contactImported.get() || readOnly}
                        limit={50}
                    />
                </Col>
                <Col>
                    <TextField
                        id="fax"
                        name="fax"
                        label={branding.configuration.printPage.sctContactInformation.lblFax}
                        placeholder={branding.configuration.printPage.sctContactInformation.hntFax}
                        value={getElementFieldValue(printCatalog, "fax")}
                        onChange={makeSetHandler(printCatalog, "fax")}
                        readonly={printCatalog.contactImported.get() || readOnly}
                        limit={50}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        id="email"
                        name="email"
                        label={branding.configuration.printPage.sctContactInformation.lblEmail}
                        placeholder={branding.configuration.printPage.sctContactInformation.hntEmail}
                        validate={makeValidationHandler(printCatalog, "email")}
                        value={getElementFieldValue(printCatalog, "email")}
                        onChange={makeSetHandler(printCatalog, "email")}
                        readonly={printCatalog.contactImported.get() || readOnly}
                        limit={100}
                    />
                </Col>
                <Col>
                    <TextField
                        id="web"
                        name="web"
                        label={branding.configuration.printPage.sctContactInformation.lblWebsite}
                        placeholder={branding.configuration.printPage.sctContactInformation.hntWebsite}
                        value={getElementFieldValue(printCatalog, "web")}
                        onChange={makeSetHandler(printCatalog, "web")}
                        readonly={printCatalog.contactImported.get() || readOnly}
                        limit={255}
                    />
                </Col>
            </Row>
        </Section>
    )
}

export default ContactSection
